import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableFooter,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import PaymentIcon from '@mui/icons-material/Payment';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

function PaymentsDashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [paymentPlans, setPaymentPlans] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [vehicleStatus, setVehicleStatus] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [planToComplete, setPlanToComplete] = useState(null);

  useEffect(() => {
    fetchPaymentPlans();
    fetchInventory();
  }, []);

  const fetchPaymentPlans = async () => {
    try {
      const username = localStorage.getItem('username');
      console.log('Fetching payment plans for username:', username);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments?username=${username}`);
      console.log('Payment plans response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Payment plans data received:', data);
        setPaymentPlans(data.plans || []);
      } else {
        console.error('Failed to fetch payment plans:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching payment plans:', error);
    }
  };

  const fetchInventory = async () => {
    try {
      const username = localStorage.getItem('username');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/inventory?username=${username}`);
      if (response.ok) {
        const data = await response.json();
        setInventory(data.inventory || []);
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredInventory = inventory.filter(vehicle => {
    if (vehicleStatus !== 'all' && vehicle.sale_status !== vehicleStatus) return false;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      vehicle.make?.toLowerCase().includes(searchLower) ||
      vehicle.model?.toLowerCase().includes(searchLower) ||
      vehicle.vin?.toLowerCase().includes(searchLower) ||
      vehicle.purchase_date?.includes(searchTerm)
    );
  });

  const filteredPaymentPlans = paymentPlans.filter(plan => {
    if (filterStatus !== 'all' && plan.status !== filterStatus) return false;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      plan.vehicle_make?.toLowerCase().includes(searchLower) ||
      plan.vehicle_model?.toLowerCase().includes(searchLower) ||
      plan.vin?.toLowerCase().includes(searchLower) ||
      plan.customer_name?.toLowerCase().includes(searchLower)
    );
  });

  const handleAddPaymentPlan = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowAddDialog(false);
    navigate(`/payments/${vehicle.vin}`);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const handleCompletePlan = async () => {
    if (!planToComplete) return;
    
    try {
      const username = localStorage.getItem('username');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payments/${planToComplete.vin}/complete?username=${username}`, {
        method: 'PUT',
      });
      
      if (response.ok) {
        fetchPaymentPlans(); // Refresh the data
        setShowCompleteDialog(false);
        setPlanToComplete(null);
      } else {
        const data = await response.json();
        console.error('Failed to complete payment plan:', data.error);
        alert('Failed to complete payment plan: ' + data.error);
      }
    } catch (error) {
      console.error('Error completing payment plan:', error);
      alert('Error completing payment plan');
    }
  };

  const openCompleteDialog = (plan) => {
    setPlanToComplete(plan);
    setShowCompleteDialog(true);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 2,
            background: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.8)
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 2px 12px rgba(0,0,0,0.1)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PaymentIcon sx={{ fontSize: 40, mr: 2, color: theme.palette.primary.main }} />
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                Payment Plans
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddDialog(true)}
              sx={{
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                borderRadius: 2
              }}
            >
              New Payment Plan
            </Button>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by make, model, VIN, or customer name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Payment Status"
                >
                  <MenuItem value="all">All Plans</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="defaulted">Defaulted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Vehicle Status</InputLabel>
                <Select
                  value={vehicleStatus}
                  onChange={(e) => setVehicleStatus(e.target.value)}
                  label="Vehicle Status"
                >
                  <MenuItem value="all">All Vehicles</MenuItem>
                  <MenuItem value="available">Available</MenuItem>
                  <MenuItem value="sold">Sold</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{
                  '& th': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.primary.light, 0.1),
                    fontWeight: 'bold',
                  }
                }}>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Total Amount</TableCell>
                  <TableCell>Uncollected</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Payment</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography>Loading payment plans...</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredPaymentPlans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {paymentPlans.length === 0 ? 'No payment plans found' : 'No payment plans match your search criteria'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPaymentPlans.map((plan) => (
                    <TableRow key={plan.vin} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">{plan.vehicle_make} {plan.vehicle_model}</Typography>
                          <Typography variant="caption" color="text.secondary">VIN: {plan.vin}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{plan.customer_name}</TableCell>
                      <TableCell>{formatCurrency(plan.total_amount)}</TableCell>
                      <TableCell>{formatCurrency(plan.remaining_balance || 0)}</TableCell>
                      <TableCell>
                        <Chip
                          label={plan.status}
                          color={
                            plan.status === 'active' ? 'success' :
                            plan.status === 'completed' ? 'info' :
                            'error'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {plan.last_payment_date ? format(new Date(plan.last_payment_date), 'MM/dd/yyyy') : 'No payments'}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate(`/payments/${plan.vin}`)}
                          >
                            Make Payment
                          </Button>
                          {plan.status === 'active' && (
                            <Button
                              variant="contained"
                              size="small"
                              color="success"
                              onClick={() => openCompleteDialog(plan)}
                            >
                              Mark Complete
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ 
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? alpha(theme.palette.primary.main, 0.15) 
                    : alpha(theme.palette.primary.light, 0.15),
                  '& td': { 
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    borderTop: `2px solid ${theme.palette.primary.main}`
                  }
                }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>TOTALS</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    Uncollected: {formatCurrency(
                      filteredPaymentPlans.reduce((sum, plan) => sum + (plan.remaining_balance || 0), 0)
                    )}
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Dialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Select Vehicle for Payment Plan</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  placeholder="Search inventory..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Vehicle Status</InputLabel>
                  <Select
                    value={vehicleStatus}
                    onChange={(e) => setVehicleStatus(e.target.value)}
                    label="Vehicle Status"
                  >
                    <MenuItem value="all">All Vehicles</MenuItem>
                    <MenuItem value="available">Available</MenuItem>
                    <MenuItem value="sold">Sold</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TableContainer sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Vehicle</TableCell>
                    <TableCell>VIN</TableCell>
                    <TableCell>Purchase Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredInventory.map((vehicle) => (
                    <TableRow key={vehicle.vin} hover>
                      <TableCell>
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </TableCell>
                      <TableCell>{vehicle.vin}</TableCell>
                      <TableCell>{vehicle.purchase_date}</TableCell>
                      <TableCell>
                        <Chip 
                          label={vehicle.sale_status} 
                          color={vehicle.sale_status === 'available' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatCurrency(vehicle.purchase_price)}</TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleAddPaymentPlan(vehicle)}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showCompleteDialog}
        onClose={() => setShowCompleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">Complete Payment Plan</Typography>
        </DialogTitle>
        <DialogContent>
          {planToComplete && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to mark this payment plan as completed?
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>Payment Plan Details:</Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Vehicle: {planToComplete.vehicle_make} {planToComplete.vehicle_model}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Customer: {planToComplete.customer_name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Total Amount: {formatCurrency(planToComplete.total_amount)}
                </Typography>
                <Typography variant="body2">
                  Remaining Balance: {formatCurrency(planToComplete.remaining_balance || 0)}
                </Typography>
              </Box>
              {(planToComplete.remaining_balance || 0) > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1, border: '1px solid', borderColor: 'info.main', alpha: 0.1 }}>
                  <Typography variant="body2" color="info.dark">
                    Note: This payment plan still has a remaining balance. 
                    Marking it as complete will close the plan regardless of outstanding payments.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCompleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleCompletePlan}
            variant="contained"
            color="success"
          >
            Mark Complete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default PaymentsDashboard; 